import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { useNavigation } from '../../hooks/useNavigation';
import { FAQ, Footer, CommitmentModal } from '../ui';
import { CANDIDATE_ENDPOINTS } from '../../constants/apiEndpoints';

function WelcomeScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { getAppropriateStep } = useNavigation();
  
  // Get eid from URL path parameter
  const eid = params.eid || '';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [userData, setUserData] = useState({});
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [showCommitmentModal, setShowCommitmentModal] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  
  // Auto-rotate testimonials every x seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        setFadeIn(true);
      }, 300); // Wait for fade out before changing
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Handle manual testimonial change
  const handleTestimonialChange = (index) => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentTestimonial(index);
      setFadeIn(true);
    }, 300);
  };
  
  // Testimonials data
  const testimonials = [
    {
      text: "Honestly I was surprised how smooth everything went. The interviewer actually used what I did in the test instead of going through my CV line by line like they usually do.",
      name: "Lydia G.",
      title: "Software Engineer",
      image: "/img/candidate3.png"
    },
    {
      text: "I liked this assessment way more than typical theoretical ones. I'm a practical person, and this format just made more sense, it felt much closer to real-world development.",
      name: "Goran F.",
      title: "Full-Stack JS Developer",
      image: "/img/candidate2.png"
    },
    {
      text: "I'm certainly not a fan of tests, but I'd still rather do a short one and get seen, instead of getting filtered out by some system that didn't like my wording.",
      name: "Iulia B.",
      title: "Senior QA Engineer",
      image: "/img/candidate4.png"
    },
    {
      text: "Most other platforms have complex UIs. This one was intuitive and much easier to work with, very dev friendly and in my opinion better job context than HackerRank or TestGorilla.",
      name: "Awais K.",
      title: "Senior Python Engineer",
      image: "/img/candidate1.png"
    }
  ];
  
  // Get session context with loading state
  const { initializeSession, validateSession, sessionData, isLoading } = useSession();

  // Check if session is already valid
  useEffect(() => {
    // Only check validation if loading is complete
    if (!isLoading) {
      if (validateSession()) {
        setSessionInitialized(true);
        // We'll still fetch fresh data from the server even if session is valid
      }
    }
  }, [validateSession, isLoading]);

  useEffect(() => {
    // Fetch data from the server when the component mounts to get the latest session state
    if (isLoading || dataFetched) return;
    
    const fetchUserData = async () => {
      try {
        // Set dataFetched to true to prevent multiple fetches
        setDataFetched(true);
        
        const response = await fetch(`${CANDIDATE_ENDPOINTS.GET_DATA}?eid=${eid}`);
        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }
        const responseData = await response.json();
        
        // Handle the new data structure (1-item array)
        const data = responseData[0];
        
        console.log("Data loaded: ", data);

        // Validate the data (email is not mandatory)
        if (!data.fname) {
          throw new Error('Invalid data received');
        }
        
        const userDataObj = {
          fname: data.fname,
          lname: data.lname,
          email: data.email,
          company: data.job_company_name,
          logo: data.job_company_logo,
          job: data.job_name,
          status: data.status,
          challenge_status: data.challenge_status,
          questionnaire_status: data.questionnaire_status,
          is_job_evaluation: !!data.candidate_id,
          resumeReceived: data.resumeReceived,
          intro_video_received: data.intro_video_received,
          questionnaire_max_duration_minutes: data.questionnaire_max_duration_minutes,
          challenge_max_duration_minutes: data.challenge_max_duration_minutes,
          target_questionnaire_questions: data.target_questionnaire_questions,
          target_challenge_questions: data.target_challenge_questions,
          sys_company_logo_url: data.sys_company_logo_url,
          sys_company_name: data.sys_company_name,
          evaluation_completed_content: data.evaluation_completed_content,
          countdown_enabled: data.countdown_enabled,
          steps: data.steps,
          challenge_type: data.challenge_type
        };
        
        setUserData(userDataObj);
        
        // Initialize the session with the user data
        const success = initializeSession(userDataObj, eid);
        
        if (success) {
          setSessionInitialized(true);
        } else {
          throw new Error('Failed to initialize session');
        }
        
        // Only set loading to false if we have all required data
        if (userDataObj.fname) {
          setLoading(false);
        } else {
          throw new Error('Missing required user data fields');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(true);
        setLoading(false); // Always set loading to false when there's an error
      }
    };

    if (eid) {
      fetchUserData();
    } else {
      // If no eid is provided, show an error
      setError(true);
      setLoading(false);
    }
    
    // No cleanup function needed as we're not using isMounted flag
  }, [eid, initializeSession, isLoading]);

  // Update page title when userData changes
  useEffect(() => {
    if (userData.fname) {
      // Use lname if available, otherwise just use fname
      const fullName = userData.lname ? `${userData.fname} ${userData.lname}` : userData.fname;
      document.title = `${fullName} - Evaluation`;
    }
  }, [userData.fname, userData.lname]);

  const handleStart = () => {
    // Only navigate if session is initialized
    if (sessionInitialized) {
      // Use navigation system to find the most appropriate step
      const appropriateStep = getAppropriateStep();
      if (appropriateStep) {
        const path = appropriateStep.path === '/' ? `/${eid}` : `${appropriateStep.path}/${eid}`;
        navigate(path);
        // Scroll to top after navigation
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      } else {
        // Fallback to resume if no appropriate step found
        navigate(`/resume/${eid}`);
        // Scroll to top after navigation
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }
    } else {
      setError(true);
    }
  };

  // No redirection here - we want the welcome page to always be accessible
  // to fetch the latest session data from the server
  
  // First check for error state - this should take precedence
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg border border-red-100">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Data</h2>
          <p className="text-gray-700 mb-6">
            {eid 
              ? "We couldn't load your evaluation details. Please try refreshing the page or contact support if the problem persists."
              : "We couldn't load your evaluation details. Please try refreshing the page or contact support if the problem persists."}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-colors duration-200 shadow-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Then check for loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="inline-block h-20 w-20 animate-spin rounded-full border-4 border-solid border-gray-500 border-r-transparent mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Loading your Evaluation</h2>
          <p className="text-gray-600 max-w-md">Please stand by while we prepare your evaluation. This should only take a moment.</p>
        </div>
      </div>
    );
  }
  
  // THIS OVERRIDES SHOWING THE AbandonedScreen in challenge or questionnaire and instead shows ONE SINGLE UNIFIED abandoned message screen here
  // Check if either challenge_status or questionnaire_status is "cs_abandoned" or "qs_abandoned"
  if (userData.challenge_status === "cs_abandoned" || userData.questionnaire_status === "qs_abandoned") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Evaluation Abandoned</h2>
          <p className="text-gray-700 mb-6">
            Please contact the hiring team if you think this is a mistake or require any assistance.
          </p>
          <div className="text-sm text-gray-500">
            {userData.challenge_status === "cs_abandoned" ? "Challenge expired or was abandoned" : ""}
            {userData.challenge_status === "cs_abandoned" && userData.questionnaire_status === "qs_abandoned" ? ", " : ""}
            {userData.questionnaire_status === "qs_abandoned" ? "Questionnaire expired or was abandoned" : ""}
          </div>
        </div>
      </div>
    );
  }
  

  return (
    <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
      {/* 2-Column Layout Container */}
      <div className="flex-1 flex justify-center px-4">
        <div className="w-full max-w-[1100px] bg-white rounded-3xl shadow-md flex">
          {/* Left Column - Main Content */}
          <div className="flex-1 py-12 px-8 flex flex-col items-center relative border-r border-gray-200">
        {/* Company Logo */}
        <div className="w-full max-w-2xl flex items-center justify-center mb-10 px-4" style={{ minHeight: '128px' }}>
        {userData.is_job_evaluation && userData.logo ? (
          <img src={userData.logo} alt={`${userData.company} logo`} className="max-w-full max-h-32 object-contain p-2" />
        ) : userData.is_job_evaluation && userData.company ? (
          <h2 className="text-2xl font-bold text-center whitespace-nowrap">{userData.company}</h2>
        ) : userData.sys_company_logo_url ? (
          <img src={userData.sys_company_logo_url} alt={`${userData.sys_company_name || 'Company'} logo`} className="max-w-full max-h-32 object-contain p-2" />
        ) : userData.sys_company_name ? (
          <h2 className="text-2xl font-bold text-center whitespace-nowrap">{userData.sys_company_name}</h2>
        ) : (
          <svg width="196" height="40" viewBox="0 0 98 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M44.0335 8.3916C44.0335 5.30684 46.5756 2.79719 49.7002 2.79719C52.8248 2.79719 55.3669 5.30684 55.3669 8.3916C55.3669 11.4764 52.8248 13.986 49.7002 13.986C46.5756 13.986 44.0335 11.4764 44.0335 8.3916ZM44.1165 2.06495C44.0775 2.09852 44.0168 2.08138 44.0023 2.03236L43.6083 0.699284H41.2V20H44.0333V14.6458C45.5371 15.9747 47.5229 16.7832 49.7 16.7832C54.5042 16.7832 58.3775 12.8485 58.1937 8.06516C58.0231 3.62194 54.3131 0.0566964 49.8096 0.000682362C47.6296 -0.0264505 45.6355 0.757187 44.1165 2.06495ZM98 2.54245C97.2332 1.76026 96.3553 1.16052 95.4184 0.742459C94.37 0.265799 93.2093 0 91.9875 0C89.8284 0 87.909 0.776598 86.4485 2.06249C86.4088 2.09742 86.3474 2.07805 86.3346 2.0262L86.0109 0.717213H83.55V17.5H86.3625V12.4756V10.0659C86.3625 8.03515 86.4835 6.07336 88.0164 4.56614C88.8448 3.75153 89.9178 3.18923 91.0509 2.98397C92.2066 2.77461 93.4146 2.94122 94.473 3.45962C95.0425 3.73862 95.5644 4.11523 96.0113 4.57101L98 2.54245ZM13.3413 0.874512V8.61564C13.3413 11.4973 10.9788 13.8416 8.075 13.8416C5.17116 13.8416 2.8087 11.4973 2.8087 8.61564V0.874512H0V8.61564C0 13.0548 3.62244 16.6665 8.075 16.6665C12.5276 16.6665 16.15 13.0548 16.15 8.61564V0.874512H13.3413ZM76.5032 7.53241L64.5737 7.51374C64.8323 6.43407 65.3945 5.44394 66.2178 4.64272C67.3652 3.52592 68.8812 2.91269 70.4915 2.91269L70.5388 2.91283C74.1058 2.93952 76.0033 5.08288 76.5032 7.53241ZM70.4567 17.6401C70.5185 17.6432 70.6229 17.647 70.7634 17.647C71.9571 17.647 75.7493 17.3724 78.0195 14.0616L75.689 12.5149C73.966 15.0279 70.7918 14.8892 70.6002 14.878L70.5053 14.8745C67.6287 14.8654 65.2161 12.9038 64.577 10.2795L79.4454 10.3027L79.4499 8.92211C79.4577 6.53868 78.6148 4.34982 77.0763 2.75878C75.4434 1.06996 73.19 0.166817 70.56 0.147249C70.5368 0.147042 70.5139 0.146973 70.4906 0.146973C68.1356 0.146973 65.9181 1.04383 64.2405 2.67643C62.5456 4.32604 61.6079 6.52395 61.6001 8.86534C61.5843 13.6752 65.5537 17.6034 70.4567 17.6401ZM28.6001 2.79719C25.4755 2.79719 22.9335 5.30684 22.9335 8.3916C22.9335 11.4764 25.4755 13.986 28.6001 13.986C31.7248 13.986 34.2668 11.4764 34.2668 8.3916C34.2668 5.30684 31.7248 2.79719 28.6001 2.79719ZM22.9023 2.03236C22.9167 2.08138 22.9774 2.09852 23.0165 2.06495C24.5354 0.757187 26.5295 -0.0264505 28.7096 0.000682362C33.213 0.0566964 36.9231 3.62194 37.0937 8.06516C37.2774 12.8485 33.4041 16.7832 28.6 16.7832C26.4229 16.7832 24.4371 15.9747 22.9333 14.6458V20H20.1V0.699284H22.5083L22.9023 2.03236Z" fill="#0F44D7"></path>
          </svg>
        )}
      </div>
      
      {/* Greeting and Title */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold mb-3">
          Welcome {userData.fname}!
        </h1>
        
        <p className="text-xl text-gray-700 max-w-xl leading-relaxed">
          {userData.is_job_evaluation ? (
            <>Please follow these steps to complete your Evaluation for the <span className="font-semibold">{userData.job}</span> role at <span className="font-semibold">{userData.company}</span></>
          ) : (
            <>Please follow these steps to complete your Evaluation.</>
          )}
        </p>
      </div>
      
      {/* Steps List */}
      <div className="w-full max-w-2xl mx-auto mb-10 px-4">
        <div className="flex flex-col space-y-6 items-center">
          {(() => {
            // Define all possible steps with their configurations
            const allSteps = [
              {
                key: 'step_upload_resume',
                title: 'Resume',
                description: 'Share your professional experience',
                icon: null,
                badge: {
                  icon: (
                    <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                    </svg>
                  ),
                  text: 'PDF'
                }

              },
              {
                key: 'step_video_intro',
                title: 'Video Intro',
                description: 'Introduce yourself in a short video',
                icon: null,
                badge: {
                  icon: (
                    <svg className="w-4 h-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="currentColor" />
                    </svg>
                  ),
                  text: '90 sec'
                }
              },
              {
                key: 'step_questionnaire',
                title: 'Questionnaire',
                description: 'Tell us about your background and experience',
                icon: null,
                badge: (() => {
                  const badges = [];
                  
                  // Add question count badge if available
                  if (userData.target_questionnaire_questions) {
                    badges.push({
                      text: `${userData.target_questionnaire_questions} questions`
                    });
                  }
                  
                  // Add time badge if countdown is enabled and duration is available
                  if (userData.countdown_enabled && userData.questionnaire_max_duration_minutes) {
                    badges.push({
                      text: `${userData.questionnaire_max_duration_minutes} min`
                    });
                  }
                  
                  return badges.length > 0 ? badges : null;
                })()
              },
              {
                key: 'step_challenge',
                title: 'Challenge',
                description: 'Demonstrate your skills',
                icon: null,
                badge: (() => {
                  const badges = [];
                  
                  // Add question count badge if available
                  if (userData.target_challenge_questions) {
                    badges.push({
                      text: `${userData.target_challenge_questions} questions`
                    });
                  }
                  
                  // Add time badge if countdown is enabled and duration is available
                  if (userData.countdown_enabled && userData.challenge_max_duration_minutes) {
                    badges.push({
                      text: `${userData.challenge_max_duration_minutes} min`
                    });
                  }
                  
                  return badges.length > 0 ? badges : null;
                })()
              }
            ];

            // Filter steps based on what's enabled in userData.steps
            const enabledSteps = allSteps.filter(step => userData.steps && userData.steps[step.key]);
            
            // Add the final "That's all!" step
            enabledSteps.push({
              key: 'final',
              title: "That's all!",
              description: userData.is_job_evaluation 
                ? `${userData.company} will review and be in touch`
                : "Your results will be reviewed and we'll be in touch",
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              ),
              isFinal: true
            });

            return enabledSteps.map((step, index) => (
              <div key={step.key} className="flex items-start w-full max-w-md">
                <div className={`flex-shrink-0 rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold ${
                  step.isFinal 
                    ? 'bg-white text-black border-2 border-black' 
                    : 'bg-black text-white'
                }`}>
                  {step.icon || (index + 1)}
                </div>
                <div className="ml-4 flex-grow">
                  <div className="flex items-center">
                    <h3 className="text-xl font-semibold text-gray-800">{step.title}</h3>
                    {step.badge && (
                      Array.isArray(step.badge) ? (
                        // Multiple badges
                        step.badge.map((badge, badgeIndex) => (
                          <div key={badgeIndex} className="ml-3 bg-gray-100 rounded-full px-3 py-1 flex items-center">
                            <span className="font-regular text-sm">{badge.text}</span>
                          </div>
                        ))
                      ) : (
                        // Single badge
                        <div className="ml-3 bg-gray-100 rounded-full px-3 py-1 flex items-center">
                          <span className="font-regular text-sm">{step.badge.text}</span>
                        </div>
                      )
                    )}
                  </div>
                  <p className="text-gray-600 mt-1">{step.description}</p>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
      
      {/* Start Button */}
      <button 
        onClick={handleStart} 
        className="px-16 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xl font-bold rounded-lg hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg mb-12 flex items-center"
      >
        Let's Go!
        <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
        </svg>
      </button>
      
      {/* Time Information */}
      {userData.countdown_enabled && (
        <div className="w-full max-w-2xl mx-auto mb-12 bg-blue-50 rounded-lg p-5 border border-blue-100 shadow-sm">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-blue-700 text-lg">
              Please ensure you set aside sufficient time for this evaluation. 
              On average others spend {Math.round((userData.questionnaire_max_duration_minutes + userData.challenge_max_duration_minutes) * 0.8)} minutes on this evaluation.
            </p>
          </div>
        </div>
      )}
      
        {/* FAQ Section */}
        <FAQ faqType="welcome" />
          </div>
          
          {/* Right Column - Testimonials Sidebar */}
          <div 
            className="w-80 py-10 px-8 rounded-r-3xl flex flex-col justify-between relative" 
            style={{ 
              backgroundColor: '#F9F9F9',
              backgroundImage: 'url(/leftbg.svg), url(/hotcircle.svg)',
              backgroundSize: '120% auto, auto',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'top center, bottom center'
            }}
          >
            {/* Testimonial Content */}
            <div className="flex-1 flex flex-col justify-center px-2">
              <div className={`transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                <p className="text-gray-800 italic text-center mb-8 leading-relaxed text-lg">
                  {testimonials[currentTestimonial].text}
                </p>
                
                {/* User Info */}
                <div className="flex flex-col items-center mb-8">
                  <img 
                    src={testimonials[currentTestimonial].image} 
                    alt={testimonials[currentTestimonial].name}
                    className="w-16 h-16 rounded-full object-cover mb-3"
                  />
                  <h4 className="font-bold text-gray-900 text-lg">{testimonials[currentTestimonial].name}</h4>
                  <p className="text-sm text-gray-600">{testimonials[currentTestimonial].title}</p>
                </div>
              </div>
              
              {/* Selector Bubbles */}
              <div className="flex justify-center gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleTestimonialChange(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      index === currentTestimonial 
                        ? 'bg-teal-500 w-8' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`View testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            
            {/* Our Commitment Button */}
            <div className="mt-6">
              <button
                onClick={() => setShowCommitmentModal(true)}
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-green-600 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Our Promise To You
              </button>
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-300 pt-4 mt-6">
              <div className="text-xs text-gray-500 text-center">
                Powered by tendent.ai
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Commitment Modal */}
      <CommitmentModal 
        isOpen={showCommitmentModal} 
        onClose={() => setShowCommitmentModal(false)} 
      />
    </div>
  );
}

export default WelcomeScreen;
